/**
 * HardwareSettings - Configuration du matériel (imprimante, balance, scanner)
 */
import { useState } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { PrinterAdapterFactory } from '../../services/printers/PrinterAdapterFactory';
import { generateSaleReceipt } from '../../services/printers/receiptGenerator';
import type { PrinterConfig } from '../../services/printers/PrinterAdapter';
import type { Sale } from '../../types/pos.types';
import { logger } from '../../utils/logger';

export default function HardwareSettings() {
  const { tenant, reloadTenant } = useTenant();
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // Configuration imprimante depuis le tenant
  const printerConfig = tenant?.config?.hardware?.printer || {
    enabled: false,
    type: 'BROWSER_PRINT',
    settings: {},
  };

  // Informations magasin
  const storeInfo = {
    name: tenant?.config?.storeInfo?.name || tenant?.name || '',
    address: tenant?.config?.storeInfo?.address || '',
    phone: tenant?.config?.storeInfo?.phone || '',
    taxNumber: tenant?.config?.storeInfo?.taxNumber || '',
    iban: tenant?.config?.storeInfo?.iban || '',
  };

  // Options du ticket
  const receiptOptions = tenant?.config?.hardware?.printer?.receiptOptions || {
    showBarcode: false,
    showQRCode: false,
    footer: 'Merci de votre visite !',
    copies: 1,
    openCashDrawer: false,
  };

  const handleTestPrinter = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      // Créer un adapter basé sur la config
      const config: PrinterConfig = {
        model: printerConfig.type as any || 'BROWSER_PRINT',
        connection: printerConfig.type === 'BROWSER_PRINT' ? 'BROWSER' : 'USB',
        settings: printerConfig.settings,
      };

      const adapter = await PrinterAdapterFactory.createAsync(config);
      await adapter.connect();

      // Obtenir le statut
      const status = await adapter.getStatus();

      if (!status.online) {
        throw new Error('Imprimante hors ligne');
      }

      if (status.paperStatus === 'empty') {
        throw new Error('Papier vide');
      }

      // Créer une vente de test
      const testSale: Sale = {
        id: 'test-' + Date.now(),
        sale_number: 'TEST-001',
        vendor_id: 'TEST',
        customer_id: null,
        status: 'completed',
        payment_method: 'cash',
        total_ht_cents: 833,
        total_ttc_cents: 1000,
        total_tva_cents: 167,
        created_at: new Date().toISOString(),
        lines: [
          {
            id: '1',
            sale_id: 'test-1',
            product_id: 'test-product',
            product_name: 'Produit de test',
            quantity: 2,
            unit_price_cents: 500,
            tax_rate: 0.06,
            is_deposit: false,
            is_return: false,
          },
        ],
      };

      // Générer et imprimer le ticket de test
      const receipt = generateSaleReceipt(testSale, {
        storeName: storeInfo.name,
        storeAddress: storeInfo.address || undefined,
        storePhone: storeInfo.phone || undefined,
        storeTaxNumber: storeInfo.taxNumber || undefined,
        showBarcode: receiptOptions.showBarcode,
        showQRCode: receiptOptions.showQRCode,
        footer: receiptOptions.footer,
      });

      await adapter.print(receipt);

      // Ouvrir le tiroir-caisse si activé et supporté
      if (receiptOptions.openCashDrawer && adapter.openCashDrawer) {
        await adapter.openCashDrawer();
      }

      await adapter.disconnect();

      setTestResult({
        success: true,
        message: 'Impression de test réussie ! Vérifiez votre imprimante.',
      });
    } catch (error) {
      logger.error('[HardwareSettings] Erreur test impression:', error);
      setTestResult({
        success: false,
        message: error instanceof Error ? error.message : 'Erreur inconnue',
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!tenant?.id) {
      alert('Erreur: Aucun tenant sélectionné');
      return;
    }

    try {
      // Construire l'objet de configuration
      const config = {
        hardware: {
          printer: {
            enabled: (document.querySelector('[name="printer.enabled"]') as HTMLInputElement)?.checked ?? printerConfig.enabled,
            type: (document.querySelector('[name="printer.type"]') as HTMLSelectElement)?.value ?? printerConfig.type,
            settings: printerConfig.settings,
            receiptOptions: {
              showBarcode: (document.querySelector('[name="receipt.showBarcode"]') as HTMLInputElement)?.checked ?? false,
              showQRCode: (document.querySelector('[name="receipt.showQRCode"]') as HTMLInputElement)?.checked ?? false,
              footer: (document.querySelector('[name="receipt.footer"]') as HTMLTextAreaElement)?.value ?? 'Merci de votre visite !',
              copies: parseInt((document.querySelector('[name="receipt.copies"]') as HTMLInputElement)?.value ?? '1'),
              openCashDrawer: (document.querySelector('[name="receipt.openCashDrawer"]') as HTMLInputElement)?.checked ?? false,
            },
          },
        },
        storeInfo: {
          name: (document.querySelector('[name="store.name"]') as HTMLInputElement)?.value ?? storeInfo.name,
          address: (document.querySelector('[name="store.address"]') as HTMLInputElement)?.value ?? storeInfo.address,
          phone: (document.querySelector('[name="store.phone"]') as HTMLInputElement)?.value ?? storeInfo.phone,
          taxNumber: (document.querySelector('[name="store.taxNumber"]') as HTMLInputElement)?.value ?? storeInfo.taxNumber,
          iban: (document.querySelector('[name="store.iban"]') as HTMLInputElement)?.value ?? storeInfo.iban,
        },
      };

      logger.log('[HardwareSettings] 📤 Sauvegarde config pour tenant:', tenant.id);
      logger.log('[HardwareSettings] 📦 Config à envoyer:', JSON.stringify(config, null, 2));

      // Appeler l'API pour la config tenant
      const { api } = await import('../../services/api');
      const result = await api.updateTenantConfig(tenant.id, config);

      // Sauvegarder aussi la configuration Zebra (directement dans les champs du tenant)
      const connectionType = (document.querySelector('[name="zebra.connectionType"]') as HTMLSelectElement)?.value || 'usb';
      const zebraConfig: any = {
        connectionType: connectionType as 'usb' | 'network',
        labelWidth: parseInt((document.querySelector('[name="zebra.labelWidth"]') as HTMLSelectElement)?.value || '102'),
        labelHeight: parseInt((document.querySelector('[name="zebra.labelHeight"]') as HTMLSelectElement)?.value || '152'),
        labelDpi: parseInt((document.querySelector('[name="zebra.dpi"]') as HTMLSelectElement)?.value || '203'),
      };

      // Ajouter les champs spécifiques selon le type de connexion
      if (connectionType === 'usb') {
        zebraConfig.usbQueueName = (document.querySelector('[name="zebra.usbQueueName"]') as HTMLInputElement)?.value;
      } else {
        zebraConfig.ip = (document.querySelector('[name="zebra.ip"]') as HTMLInputElement)?.value;
        zebraConfig.port = parseInt((document.querySelector('[name="zebra.port"]') as HTMLInputElement)?.value || '9100');
      }

      // Sauvegarder seulement si on a une config valide
      if ((connectionType === 'usb' && zebraConfig.usbQueueName) || (connectionType === 'network' && zebraConfig.ip)) {
        await api.patch('/zebra-printing/config', zebraConfig, {
          headers: { 'X-Tenant-ID': tenant.id },
        });
      }

      logger.log('[HardwareSettings] ✅ Réponse API:', result);
      alert('Configuration sauvegardée avec succès !');

      logger.log('[HardwareSettings] 🔄 Rechargement du tenant...');
      await reloadTenant();
      logger.log('[HardwareSettings] ✅ Tenant rechargé');
    } catch (error) {
      logger.error('[HardwareSettings] ❌ Erreur sauvegarde:', error);
      alert(`Erreur lors de la sauvegarde: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    }
  };

  // Utiliser une key basée sur le tenant pour forcer le re-render complet
  const formKey = `${tenant?.id}-${tenant?.updated_at || Date.now()}`;

  return (
    <div className="space-y-8" key={formKey}>
      {/* ========== IMPRIMANTE ========== */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Imprimante de tickets</h2>
            <p className="text-sm text-gray-500 mt-1">
              Configuration de l'imprimante EPSON pour les tickets de caisse
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              name="printer.enabled"
              className="sr-only peer"
              defaultChecked={printerConfig.enabled}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
          </label>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 space-y-6">
          {/* Type d'imprimante */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type d'imprimante *
            </label>
            <select
              name="printer.type"
              defaultValue={printerConfig.type || 'BROWSER_PRINT'}
              disabled={!printerConfig.enabled}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="BROWSER_PRINT">Impression système (Safari/iPad compatible)</option>
              <option value="EPSON_TM_M267D">EPSON TM-M267D via Backend API ⭐ (Recommandé)</option>
              <option value="EPSON_TM_T20">EPSON TM-T20 (USB Direct - Chrome uniquement)</option>
              <option value="EPSON_TM_M30">EPSON TM-M30 (USB Direct - Chrome uniquement)</option>
              <option value="EPSON_TM_T88">EPSON TM-T88 (USB Direct - Chrome uniquement)</option>
            </select>
            <div className="mt-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="text-xs text-emerald-800">
                <strong>✅ Recommandé :</strong> L'option "EPSON TM-M267D via Backend API" fonctionne avec tous les navigateurs (Chrome, Safari, Firefox, etc.)
                et ne nécessite aucune permission USB dans le navigateur. L'impression est gérée par le serveur Node.js via CUPS.
              </p>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              ℹ️ Les options "USB Direct" nécessitent Chrome et des permissions Web USB
            </p>
          </div>

          {/* Détection imprimante CUPS (pour EPSON_TM_M267D) */}
          {printerConfig.type === 'EPSON_TM_M267D' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Configuration automatique CUPS
              </label>
              <button
                onClick={async () => {
                  try {
                    const { api } = await import('../../services/api');
                    const result = await api.get<{
                      success: boolean;
                      printers: Array<{ name: string; status: string; description: string }>;
                      message: string;
                    }>('/printing/detect-cups');

                    if (result.success && result.printers.length > 0) {
                      // Filtrer les imprimantes EPSON de type ticket (TM série)
                      const epsonPrinters = result.printers.filter(p =>
                        p.name.toUpperCase().includes('EPSON') &&
                        (p.name.toUpperCase().includes('TM') || p.name.toUpperCase().includes('M267'))
                      );

                      if (epsonPrinters.length > 0) {
                        const queueName = epsonPrinters[0].name;

                        // Sauvegarder automatiquement la première imprimante EPSON trouvée
                        // L'interceptor ajoutera automatiquement le header X-Tenant-ID
                        await api.patch('/epson-ticket/config', {
                          connectionType: 'cups',
                          cupsQueueName: queueName,
                        });

                        alert(`✅ Imprimante EPSON détectée et configurée : ${queueName}`);
                        await reloadTenant();
                      } else {
                        alert('⚠️ Aucune imprimante EPSON de type ticket (TM) trouvée. Imprimantes disponibles : ' + result.printers.map(p => p.name).join(', '));
                      }
                    } else {
                      alert('❌ ' + result.message);
                    }
                  } catch (error: any) {
                    alert('❌ Erreur détection : ' + error.message);
                  }
                }}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium transition-colors text-sm"
              >
                🔍 Détecter l'imprimante CUPS
              </button>
              <p className="mt-2 text-xs text-gray-500">
                Détecte automatiquement la queue CUPS EPSON et sauvegarde la configuration
              </p>
            </div>
          )}

          {/* Test de connexion */}
          <div className="flex items-start gap-4">
            <button
              onClick={handleTestPrinter}
              disabled={!printerConfig.enabled || testing}
              className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
            >
              {testing ? '⏳ Test en cours...' : '🖨️ Test d\'impression'}
            </button>
            {testResult && (
              <div
                className={`flex-1 px-4 py-3 rounded-lg border-2 ${
                  testResult.success
                    ? 'bg-green-50 border-green-200 text-green-800'
                    : 'bg-red-50 border-red-200 text-red-800'
                }`}
              >
                {testResult.success ? '✓' : '⚠️'} {testResult.message}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ========== IMPRIMANTE ZEBRA (ÉTIQUETTES) ========== */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Imprimante Zebra (Étiquettes)</h2>
            <p className="text-sm text-gray-500 mt-1">
              Configuration de l'imprimante Zebra ZD421 pour les étiquettes de précommandes
            </p>
          </div>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 space-y-6">
          {/* Type de connexion */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type de connexion *
            </label>
            <select
              name="zebra.connectionType"
              defaultValue={tenant?.zebra_connection_type || 'usb'}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              onChange={(e) => {
                const usbFields = document.querySelector('#zebra-usb-fields') as HTMLElement;
                const networkFields = document.querySelector('#zebra-network-fields') as HTMLElement;
                if (e.target.value === 'usb') {
                  usbFields.style.display = 'block';
                  networkFields.style.display = 'none';
                } else {
                  usbFields.style.display = 'none';
                  networkFields.style.display = 'block';
                }
              }}
            >
              <option value="usb">USB (via CUPS)</option>
              <option value="network">Réseau (TCP/IP)</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              USB recommandé pour connexion directe, Réseau si l'imprimante a une IP
            </p>
          </div>

          {/* Champs USB */}
          <div
            id="zebra-usb-fields"
            style={{ display: tenant?.zebra_connection_type === 'NETWORK' ? 'none' : 'block' }}
            className="space-y-4"
          >
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom de la queue CUPS *
              </label>
              <input
                type="text"
                name="zebra.usbQueueName"
                defaultValue={tenant?.zebra_usb_queue_name || ''}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="Zebra_ZPL_Label_Printer"
              />
              <p className="mt-1 text-xs text-gray-500">
                Le nom de l'imprimante dans CUPS. Vérifiez avec: <code className="bg-gray-200 px-1 rounded">lpstat -p</code>
              </p>
            </div>
          </div>

          {/* Champs Réseau */}
          <div
            id="zebra-network-fields"
            style={{ display: tenant?.zebra_connection_type === 'NETWORK' ? 'block' : 'none' }}
            className="space-y-4"
          >
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Adresse IP de l'imprimante *
              </label>
              <input
                type="text"
                name="zebra.ip"
                defaultValue={tenant?.zebra_printer_ip || ''}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="192.168.1.100"
              />
              <p className="mt-1 text-xs text-gray-500">
                L'adresse IP réseau de votre imprimante Zebra (configurée en mode réseau)
              </p>
            </div>

            {/* Port TCP */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Port TCP
              </label>
              <input
                type="number"
                name="zebra.port"
                min="1"
                max="65535"
                defaultValue={tenant?.zebra_printer_port || 9100}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="9100"
              />
              <p className="mt-1 text-xs text-gray-500">Port par défaut : 9100</p>
            </div>
          </div>

          {/* Paramètres communs (taille et résolution) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4 border-t">
            {/* DPI */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Résolution (DPI)
              </label>
              <select
                name="zebra.dpi"
                defaultValue={tenant?.label_dpi || 203}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="203">203 DPI (Standard)</option>
                <option value="300">300 DPI (Haute résolution)</option>
              </select>
            </div>

            {/* Taille d'étiquette */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Largeur étiquette (mm)
              </label>
              <select
                name="zebra.labelWidth"
                defaultValue={tenant?.label_width_mm || 102}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="102">102 mm (4 pouces)</option>
                <option value="76">76 mm (3 pouces)</option>
                <option value="51">51 mm (2 pouces)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hauteur étiquette (mm)
              </label>
              <select
                name="zebra.labelHeight"
                defaultValue={tenant?.label_height_mm || 152}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="25">25 mm (étiquette produit)</option>
                <option value="51">51 mm (2 pouces)</option>
                <option value="76">76 mm (3 pouces)</option>
                <option value="102">102 mm (4 pouces)</option>
                <option value="152">152 mm (6 pouces)</option>
              </select>
            </div>
          </div>

          {/* Test de connexion Zebra */}
          <div className="flex items-start gap-4">
            <button
              onClick={async () => {
                if (!tenant?.id) return;
                try {
                  const { api } = await import('../../services/api');
                  const result = await api.get('/zebra-printing/test', {
                    headers: { 'X-Tenant-ID': tenant.id },
                  });
                  alert(result.success ? '✅ ' + result.message : '❌ ' + result.message);
                } catch (error: any) {
                  alert('❌ Erreur: ' + (error.response?.data?.message || error.message));
                }
              }}
              className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
            >
              🖨️ Test de connexion Zebra
            </button>
            <div className="flex-1 px-4 py-3 rounded-lg border-2 bg-blue-50 border-blue-200 text-blue-800">
              <strong>ℹ️ Info :</strong> Le test enverra une étiquette test à l'imprimante.
              Assurez-vous que l'imprimante est allumée et connectée au réseau.
            </div>
          </div>
        </div>
      </section>

      {/* ========== INFORMATIONS MAGASIN ========== */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Informations sur le ticket
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          Ces informations apparaîtront en haut du ticket de caisse
        </p>

        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Nom du magasin */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom du magasin *
              </label>
              <input
                type="text"
                name="store.name"
                defaultValue={storeInfo.name}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="Les Folies Maraîchères"
              />
            </div>

            {/* Téléphone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Téléphone
              </label>
              <input
                type="tel"
                name="store.phone"
                defaultValue={storeInfo.phone}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="+32 123 45 67 89"
              />
            </div>

            {/* Adresse */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Adresse complète
              </label>
              <input
                type="text"
                name="store.address"
                defaultValue={storeInfo.address}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="123 Rue du Commerce, 7370 Dour, Belgique"
              />
            </div>

            {/* Numéro de TVA */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Numéro de TVA / SIRET
              </label>
              <input
                type="text"
                name="store.taxNumber"
                defaultValue={storeInfo.taxNumber}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="BE 0123.456.789"
              />
            </div>

            {/* IBAN — imprimé sur les tickets payés par virement */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Numéro de compte (IBAN)
              </label>
              <input
                type="text"
                name="store.iban"
                defaultValue={storeInfo.iban}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="BE00 0000 0000 0000"
              />
              <p className="mt-1 text-xs text-gray-500">
                Imprimé sur les tickets dont le paiement est « Virement », avec le
                numéro de ticket en communication. Laissé vide, rien n'est imprimé.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== OPTIONS DU TICKET ========== */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Options du ticket</h2>
        <p className="text-sm text-gray-500 mb-4">
          Personnalisez l'apparence et le comportement de vos tickets
        </p>

        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 space-y-6">
          {/* Footer personnalisé */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message de fin de ticket
            </label>
            <textarea
              rows={2}
              name="receipt.footer"
              defaultValue={receiptOptions.footer}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="Merci de votre visite !"
            />
          </div>

          {/* Options d'affichage */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-white cursor-pointer">
              <input
                type="checkbox"
                name="receipt.showBarcode"
                defaultChecked={receiptOptions.showBarcode}
                className="w-4 h-4"
              />
              <div>
                <div className="font-medium text-gray-900">Afficher un code-barres</div>
                <div className="text-xs text-gray-500">Code-barres du numéro de ticket</div>
              </div>
            </label>

            <label className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-white cursor-pointer">
              <input
                type="checkbox"
                name="receipt.showQRCode"
                defaultChecked={receiptOptions.showQRCode}
                className="w-4 h-4"
              />
              <div>
                <div className="font-medium text-gray-900">Afficher un QR code</div>
                <div className="text-xs text-gray-500">Le client le scanne pour retrouver son ticket sur foliesboutik.be (et découvrir la boutique en ligne)</div>
              </div>
            </label>

            <label className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-white cursor-pointer">
              <input
                type="checkbox"
                name="receipt.openCashDrawer"
                defaultChecked={receiptOptions.openCashDrawer}
                className="w-4 h-4"
              />
              <div>
                <div className="font-medium text-gray-900">Ouvrir le tiroir-caisse</div>
                <div className="text-xs text-gray-500">Après chaque impression</div>
              </div>
            </label>

            <div className="p-4 border border-gray-200 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nombre de copies
              </label>
              <input
                type="number"
                name="receipt.copies"
                min="1"
                max="3"
                defaultValue={receiptOptions.copies}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
              <div className="text-xs text-gray-500 mt-1">Original + duplicatas</div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== BALANCE ========== */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Balance</h2>
            <p className="text-sm text-gray-500 mt-1">
              Configuration de la balance BAXTRAN SL (USB/RS-232)
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              defaultChecked={tenant?.config?.hardware?.scale?.enabled}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
          </label>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
          <p className="text-sm text-gray-500 italic">
            Configuration existante - Voir scale.config.ts
          </p>
        </div>
      </section>

      {/* ========== SCANNER CODE-BARRES ========== */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Scanner code-barres</h2>
            <p className="text-sm text-gray-500 mt-1">
              Scanner de codes-barres (Keyboard Wedge)
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              defaultChecked={tenant?.config?.hardware?.barcode_scanner?.enabled}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
          </label>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
          <p className="text-sm text-gray-500">
            Scanner configuré en mode Keyboard Wedge (pas de configuration supplémentaire requise)
          </p>
        </div>
      </section>

      {/* ========== ACTIONS ========== */}
      <div className="flex justify-end gap-3 pt-4 border-t-2 border-gray-200">
        <button
          onClick={handleSaveConfig}
          className="px-6 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium transition-colors"
        >
          💾 Sauvegarder la configuration
        </button>
      </div>
    </div>
  );
}
